import { useEffect, useRef, useState } from "react"

/**
 * A HEIC photo (iPhone) is converted to JPEG by a queued job a few seconds after
 * it is sent, at the same address: until then a browser can't decode it. The
 * image reloads its own address on this schedule — no other screen state to
 * refresh — before it is called unreadable.
 */
export const RETRY_DELAYS_MS = [2000, 4000, 8000, 16000]

// A signed S3 address breaks if a parameter is added (and S3 lets it be
// revalidated as is); the server's own assets, the messenger gallery cached a
// day, need one to get past the browser's cache — unique, or the bubble's
// failed attempt would answer the viewer's.
export function retryUri(uri, attempt, stamp) {
    if (!attempt || /[?&](X-Amz-Signature|Signature)=/.test(uri)) return uri
    return `${uri}${uri.includes("?") ? "&" : "?"}retry=${stamp}`
}

// Only a photo being converted is worth waiting for: a messenger photo still
// named .heic, or a media file stored without an extension while it waits.
// A broken avatar or thumbnail is unreadable at once, without four reloads.
export function canBePrepared(uri) {
    const match = /^https?:\/\/[^?#]*\/([^/?#]*)/.exec(uri ?? "")
    if (!match) return false
    const name = match[1]
    return /\.(heic|heif)$/i.test(name) || !name.includes(".")
}

/**
 * What a load error leads to. A local file (`blob:`, `file:`, `data:`) is not
 * being prepared anywhere: it is unreadable at once.
 * @returns {{status: "preparing"|"unreadable", retryIn: number|null}}
 */
export function afterLoadError(uri, attempt) {
    if (!canBePrepared(uri) || attempt >= RETRY_DELAYS_MS.length) {
        return { status: "unreadable", retryIn: null }
    }
    return { status: "preparing", retryIn: RETRY_DELAYS_MS[attempt] }
}

// The address that answered once its JPEG was ready, per photo: the plain one
// stays in the browser's cache (a day for the messenger) with the HEIC, so the
// next screen to show the photo starts from the one that worked.
const refreshed = new Map()

function initialState(uri) {
    const stamp = refreshed.get(uri)
    return stamp
        ? { uri, attempt: 1, stamp, status: "loading" }
        : { uri, attempt: 0, status: "loading" }
}

/**
 * @returns {{uri: string, status: "loading"|"preparing"|"unreadable", onError: Function, onLoad: Function}}
 */
export function useImageRetry(uri) {
    const [state, setState] = useState(() => initialState(uri))
    const timer = useRef(null)
    useEffect(() => () => clearTimeout(timer.current), [])

    // Kept per uri, so a viewer that moves on to the next image starts over.
    const current = state.uri === uri ? state : initialState(uri)

    const onError = () => {
        const { status, retryIn } = afterLoadError(uri, current.attempt)
        setState({ ...current, status })
        if (retryIn === null) return
        clearTimeout(timer.current)
        timer.current = setTimeout(
            () =>
                setState({
                    uri,
                    attempt: current.attempt + 1,
                    stamp: Date.now(),
                    status: "loading",
                }),
            retryIn
        )
    }

    const onLoad = () => {
        // A signed address is reloaded as is: nothing to remember.
        if (current.attempt > 0 && retryUri(uri, 1, 1) !== uri) {
            refreshed.set(uri, current.stamp)
        }
    }

    return {
        uri: uri && retryUri(uri, current.attempt, current.stamp),
        status: current.status,
        onError,
        onLoad,
    }
}
